import ExcelJS from "exceljs";

const HEADER_FILL: ExcelJS.Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF000000" },
};

const STRIPE_FILL: ExcelJS.Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFDEEAF6" },
};

const THIN_BORDER: Partial<ExcelJS.Borders> = {
  top: { style: "thin", color: { argb: "FFB0B0B0" } },
  left: { style: "thin", color: { argb: "FFB0B0B0" } },
  bottom: { style: "thin", color: { argb: "FFB0B0B0" } },
  right: { style: "thin", color: { argb: "FFB0B0B0" } },
};

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** `2026-08-31_053603` using local time, matching Regulations Export filenames. */
export function formatStyledExportTimestamp(date: Date = new Date()): string {
  return [
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`,
    `${pad2(date.getHours())}${pad2(date.getMinutes())}${pad2(date.getSeconds())}`,
  ].join("_");
}

export function buildStyledExportFilename(
  entityName: string,
  date: Date = new Date(),
): string {
  const title = entityName.trim() || "Export";
  return `${title} Export_${formatStyledExportTimestamp(date)}.xlsx`;
}

function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[:\\/?*[\]]/g, " ").trim();
  return (cleaned || "Export").slice(0, 31);
}

export type StyledExcelColumn = Readonly<{
  header: string;
  width?: number;
}>;

export type DownloadStyledExcelOptions = Readonly<{
  entityName: string;
  sheetName?: string;
  columns: readonly StyledExcelColumn[];
  rows: readonly (readonly string[])[];
}>;

function toUint8Array(buffer: ExcelJS.Buffer): Uint8Array {
  if (buffer instanceof Uint8Array) return buffer;
  if (buffer instanceof ArrayBuffer) return new Uint8Array(buffer);
  return Uint8Array.from(buffer as ArrayLike<number>);
}

function styleHeaderCell(cell: ExcelJS.Cell): void {
  cell.font = {
    name: "Calibri",
    size: 11,
    bold: true,
    color: { argb: "FFFFFFFF" },
  };
  cell.fill = HEADER_FILL;
  cell.alignment = { vertical: "middle", horizontal: "left" };
  cell.border = THIN_BORDER;
}

function styleDataCell(cell: ExcelJS.Cell, striped: boolean): void {
  cell.font = { name: "Calibri", size: 11, color: { argb: "FF262626" } };
  cell.alignment = { vertical: "middle", horizontal: "left", wrapText: false };
  cell.border = THIN_BORDER;
  if (striped) {
    cell.fill = STRIPE_FILL;
  }
}

function triggerBrowserDownload(data: ArrayBuffer, fileName: string): void {
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export async function downloadStyledExcel({
  entityName,
  sheetName,
  columns,
  rows,
}: DownloadStyledExcelOptions): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "AI Hub";
  const worksheet = workbook.addWorksheet(
    sanitizeSheetName(sheetName ?? entityName),
    { views: [{ state: "frozen", ySplit: 1 }] },
  );

  columns.forEach((column, columnIndex) => {
    const cell = worksheet.getCell(1, columnIndex + 1);
    cell.value = column.header;
    styleHeaderCell(cell);
    worksheet.getColumn(columnIndex + 1).width =
      column.width ?? Math.max(14, column.header.length + 4);
  });
  worksheet.getRow(1).height = 20;

  rows.forEach((values, rowIndex) => {
    const excelRowIndex = rowIndex + 2;
    const striped = rowIndex % 2 === 1;
    values.forEach((value, columnIndex) => {
      const cell = worksheet.getCell(excelRowIndex, columnIndex + 1);
      cell.value = value;
      styleDataCell(cell, striped);
    });
    worksheet.getRow(excelRowIndex).height = 18;
  });

  if (columns.length > 0) {
    const lastColumn = worksheet.getColumn(columns.length).letter;
    worksheet.autoFilter = {
      from: "A1",
      to: `${lastColumn}${Math.max(1, rows.length + 1)}`,
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const bytes = toUint8Array(buffer);
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  triggerBrowserDownload(
    copy.buffer as ArrayBuffer,
    buildStyledExportFilename(entityName),
  );
}
